// GitHub's side of the "Viewed" sync (viewedSync.svelte.ts): reading the
// viewer's states (the first page with the pull request, the rest in the
// background), the two mutations, when a failed write is tried again, when
// GitHub refuses them for the whole pull request, and where the baselines
// are kept (a runtime cache, runtime/cache.ts, in the account's IndexedDB).
import { HttpError } from '../api/cache';
import { graphql, GraphQLFailure, RateLimitError } from '../api/gh';
import { identityUser, onIdentityChange } from '../api/identity';
import { MARK_FILE_VIEWED, PR_HEAD, PR_VIEWED_PAGE, UNMARK_FILE_VIEWED, type ViewedFiles } from '../api/queries';
import { backoffMs } from '../logic/backoff';
import { cacheKey, hashInputs, openCache, type Backend, type Cache } from '../runtime/cache';
import { idbBackend } from '../runtime/idb';
import type { Baseline, ViewedState } from '../logic/viewedSync';
import { HeadMovedError, type BaselineStore, type WriteResult } from './viewedSync.svelte';

/**
 * Pages of viewed states read after the one that comes with the pull
 * request: 1,000 files in all. Files past them have no state, so they are
 * not synced. Each page is one more GraphQL request (1 point), read once
 * per head in the background.
 */
export const MAX_VIEWED_PAGES = 9;

/** Failed tries of one write before it is shown as an error. */
export const VIEWED_WRITE_TRIES = 6;

/**
 * Least time between two writes (ms): marking every file of a large pull
 * request reviewed sends at most 80 a minute, well inside GitHub's
 * secondary limits (a mutation counts five points of 900 a minute).
 */
export const VIEWED_WRITE_SPACING_MS = 750;

type Query = <T>(q: string, v: Record<string, unknown>) => Promise<T>;
type PageResult = { repository: { pullRequest: { headRefOid: string; files: ViewedFiles | null } | null } | null };

/** The states of one page. */
export function viewedStates(page: ViewedFiles): Record<string, ViewedState> {
  const states: Record<string, ViewedState> = {};
  for (const n of page.nodes) states[n.path] = n.viewerViewedState;
  return states;
}

/**
 * The pages after cursor `after` (the first page's end), up to
 * MAX_VIEWED_PAGES, one after another (each needs the last one's cursor),
 * each handed to `onPage` with when its request started as it arrives. A
 * page read at another head than `head`, or one that fails, ends the
 * reading. True when every page (up to the cap) was read.
 */
export async function readViewedPages(
  after: string,
  head: string,
  vars: Record<string, unknown>,
  onPage: (states: Record<string, ViewedState>, at: number) => void,
  query: Query = graphql,
  now: () => number = Date.now,
): Promise<boolean> {
  let cursor: string | null = after;
  for (let i = 0; cursor && i < MAX_VIEWED_PAGES; i++) {
    const at = now();
    let page: ViewedFiles | null;
    try {
      const res: PageResult = await query<PageResult>(PR_VIEWED_PAGE, { ...vars, after: cursor });
      const pr = res.repository?.pullRequest;
      page = pr && pr.headRefOid === head ? pr.files : null;
    } catch {
      page = null;
    }
    if (!page) return false;
    onPage(viewedStates(page), at);
    cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
  }
  return true;
}

type WriteAnswer = Record<'markFileAsViewed' | 'unmarkFileAsViewed', { pullRequest: { headRefOid: string } | null } | undefined>;

/**
 * Set the viewer's checkbox for one file of pull request `pullRequestId`
 * (its node ID), decided at `head`. GitHub's mutation names no head, so a
 * mark first checks the head (HeadMovedError when it moved); either answers
 * with the head GitHub reports with the change, for the caller to check
 * (null for a mark when that is unknown).
 */
export async function writeViewed(pullRequestId: string, path: string, viewed: boolean, head: string, query: Query = graphql): Promise<WriteResult> {
  if (viewed) {
    const now = (await query<{ node: { headRefOid?: string } | null }>(PR_HEAD, { id: pullRequestId })).node?.headRefOid;
    if (!now) throw new Error('the pull request was not found');
    if (now !== head) throw new HeadMovedError(head, now);
  }
  const res = await query<WriteAnswer>(viewed ? MARK_FILE_VIEWED : UNMARK_FILE_VIEWED, { input: { pullRequestId, path } });
  const landed = res[viewed ? 'markFileAsViewed' : 'unmarkFileAsViewed']?.pullRequest?.headRefOid;
  if (landed) return { head: landed };
  // No head in the answer: an unmark is right at any head; for a mark, the head is read again, and
  // only the same head says where it landed. Otherwise unknown (null), for the next read to settle.
  if (!viewed) return { head };
  try {
    const now = (await query<{ node: { headRefOid?: string } | null }>(PR_HEAD, { id: pullRequestId })).node?.headRefOid;
    return { head: now === head ? head : null };
  } catch {
    return { head: null };
  }
}

/** GraphQL error types that refuse the mutation for the pull request, not for one file. */
const REFUSALS = new Set(['FORBIDDEN', 'NOT_ACCESSIBLE', 'INSUFFICIENT_SCOPES']);

/**
 * Why GitHub (or the bridge) refuses the "Viewed" mutations for the whole
 * pull request (a permission the token or the App lacks), or null for a
 * failure of one write or one that may pass. A 403 that says when to try
 * again is a rate limit, not a refusal.
 */
export function viewedRefusal(e: unknown): string | null {
  if (e instanceof GraphQLFailure) {
    const r = e.errors.find((x) => x.type && REFUSALS.has(x.type));
    return r ? r.message : null;
  }
  if (e instanceof HttpError && e.status === 403 && e.retryAfter === null && e.resetAt === null) return e.message || 'GitHub refused it';
  return null;
}

/** Repositories (`owner/repo`, lowercased) whose pull requests GitHub refused the mutations for, this page session, with why. */
export const viewedRefused = new Map<string, string>();

/**
 * When to try a failed write again: when a rate limit ends; after
 * `backoffMs` for a failure that may pass (no answer, 5xx, 408, 429), up to
 * VIEWED_WRITE_TRIES tries; never for GitHub refusing it (a GraphQL error,
 * the bridge's 403, any other 4xx).
 */
export function viewedRetryAt(e: unknown, failures: number, now = Date.now(), random = Math.random): number | null {
  if (failures >= VIEWED_WRITE_TRIES) return null;
  if (e instanceof RateLimitError) return e.limit.until;
  const transient = e instanceof HttpError ? e.status === 0 || e.status >= 500 || e.status === 408 || e.status === 429 : e instanceof TypeError;
  return transient ? now + backoffMs(failures - 1, random) : null;
}

/** When to try a failed import again: whatever the failure (loading a file's diff may fail for a while), with backoff, up to VIEWED_WRITE_TRIES tries. */
export function viewedImportRetryAt(failures: number, now = Date.now(), random = Math.random): number | null {
  return failures >= VIEWED_WRITE_TRIES ? null : now + backoffMs(failures - 1, random);
}

/** Pull requests whose baselines are kept at most (`gc` drops the least recently written first). */
export const BASELINES_KEPT = 500;

/** Where builds before the runtime cache kept a pull request's baselines: read once, then deleted. */
const LEGACY_PREFIX = 'viewed-sync|';

function isBaselines(v: unknown): v is Record<string, Baseline> {
  return !!v && typeof v === 'object' && Object.values(v).every((b) => !!b && typeof b === 'object' && typeof (b as Baseline).head === 'string');
}

/**
 * The cache of baselines for `account` at `origin`: one record per pull
 * request, by `owner/repo#n` (lowercased), BASELINES_KEPT at most. The
 * baselines are the sync's own record, not a copy of GitHub's, so a record
 * has no maximum age and depends on no invalidation.
 */
export function baselineCache(origin: string, account: string, backend: Backend, now: () => number = Date.now): Cache<Record<string, Baseline>> {
  return openCache<Record<string, Baseline>>({
    name: 'viewed-baselines',
    key: cacheKey({ origin, account, schemaVersion: 1, inputsHash: hashInputs() }),
    decode: (raw) => (isBaselines(raw) ? raw : null),
    backend,
    limit: BASELINES_KEPT,
    now,
  });
}

/**
 * One pull request's baselines (`owner/repo#n`, lowercased) in `cache`,
 * whose records are in `backend`. Baselines an earlier build kept there
 * are taken over once. A write that could not be stored rejects.
 */
export function cachedBaselineStore(pr: string, cache: Cache<Record<string, Baseline>>, backend: Backend): BaselineStore {
  return {
    async read() {
      const kept = await cache.get(pr);
      if (kept) return kept.value;
      const legacy = await backend.get(LEGACY_PREFIX + pr).catch(() => undefined);
      if (!isBaselines(legacy)) return undefined;
      if ((await cache.write({ [pr]: legacy }, cache.begin())).ok) await backend.write([[LEGACY_PREFIX + pr, undefined]]).catch(() => {});
      return legacy;
    },
    async write(b) {
      const done = await cache.write({ [pr]: b }, cache.begin());
      if (!done.ok) throw new Error(`baselines not stored: ${done.failure.message}`);
    },
  };
}

let shared: { account: string; backend: Backend; cache: Cache<Record<string, Baseline>>; release: () => void } | null = null;
// Another account: its lease is let go (its records are its own).
onIdentityChange(() => {
  shared?.release();
  shared = null;
});

/** One pull request's baselines in the signed-in account's IndexedDB; nothing is kept while nobody is signed in. */
export function idbBaselineStore(pr: string): BaselineStore {
  const store = (): BaselineStore | null => {
    const account = identityUser();
    if (!account || typeof location === 'undefined') return null;
    if (shared?.account !== account) {
      const backend = idbBackend();
      const cache = baselineCache(location.origin, account, backend);
      // Held while this account is signed in: another build's `gc` keeps these records while this one is open.
      const release = cache.hold();
      void cache.gc();
      shared = { account, backend, cache, release };
    }
    return cachedBaselineStore(pr, shared.cache, shared.backend);
  };
  return {
    read: async () => store()?.read(),
    write: async (b) => store()?.write(b),
  };
}
